// Kollision eines achsenparallelen Quaders (AABB) gegen das Blockraster und gegen weitere
// Quader (etwa den Avatar aus Sicht eines Tiers). Reine Funktionen ohne Zustand; Avatar und
// Tiere benutzen sie gleichermaßen. Die Bewegung wird achsenweise aufgelöst (erst y, dann x,
// dann z): Wer schräg in eine Ecke läuft, wird auf der blockierten Achse gestoppt und gleitet
// auf der freien weiter. Dazu die Trennung zweier überlappender Quader (ADR 0018).

import { blockDef } from '../world/blocks.js';
import { getBlock, isSolid, type World } from '../world/chunk.js';
import type { Vec3 } from './state.js';

/** Ein achsenparalleler Quader in Weltkoordinaten, Grenzen einschließlich. */
export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** Ergebnis von `moveAabb`: neue Position und je Achse, ob ein Hindernis gestoppt hat. */
export interface MoveResult {
  pos: Vec3;
  hitX: boolean;
  hitY: boolean;
  hitZ: boolean;
}

type Axis = 'x' | 'y' | 'z';

/** Die beiden Achsen quer zur Bewegungsachse. */
const QUER: Record<Axis, [Axis, Axis]> = {
  x: ['y', 'z'],
  y: ['x', 'z'],
  z: ['x', 'y'],
};

/**
 * Spielraum, um den der Quader bei der Blockabtastung geschrumpft wird. Er fängt Rundungsreste ab,
 * damit ein Quader, der genau auf einer Blockfläche aufliegt oder an einer Wand anliegt, diesen
 * Block auf den anderen Achsen nicht als Hindernis sieht; sonst bliebe man am Boden oder an der
 * Wand hängen. Weit unter jeder sichtbaren Distanz.
 */
const EPS = 1e-6;

/** Erster und letzter Blockindex, den das Intervall [min, max] (um EPS geschrumpft) berührt. */
function blockRange(min: number, max: number): [number, number] {
  return [Math.floor(min + EPS), Math.floor(max - EPS)];
}

/**
 * Ob die Zelle (x, y, z) einen Quader blockiert, dessen Unterkante bei `unterkante` liegt.
 *
 * Neben dem Block in der Zelle zählt der Block **darunter** mit seiner Kollisionshöhe: Zaun,
 * Pfosten und Tor sind 1,5 Blöcke hoch und ragen in die Zelle darüber hinein (ADR 0022). Geprüft
 * wird das nur für die unterste Zelle, die der Quader berührt – tiefer liegende Blöcke sind selbst
 * schon Teil der Abtastung. `unterkante === null` schaltet die Verlängerung ab; das gilt für die
 * senkrechte Achse, damit man weiterhin auf ganzen Blockoberkanten landet und steht.
 */
function zelleBlockiert(world: World, x: number, y: number, z: number, unterkante: number | null): boolean {
  if (isSolid(world, x, y, z)) return true;
  if (unterkante === null || y !== Math.floor(unterkante + EPS)) return false;
  const darunter = blockDef(getBlock(world, x, y - 1, z));
  return darunter.solid && y - 1 + darunter.collisionHeight > unterkante + EPS;
}

/** Ob in Schicht `k` der Achse `axis` innerhalb der Querbereiche `u` und `v` etwas fest ist. */
function schichtFest(
  world: World,
  axis: Axis,
  k: number,
  quer: [Axis, Axis],
  u: [number, number],
  v: [number, number],
  unterkante: number | null,
): boolean {
  const p: Vec3 = { x: 0, y: 0, z: 0 };
  p[axis] = k;
  for (let i = u[0]; i <= u[1]; i++) {
    p[quer[0]] = i;
    for (let j = v[0]; j <= v[1]; j++) {
      p[quer[1]] = j;
      if (zelleBlockiert(world, p.x, p.y, p.z, unterkante)) return true;
    }
  }
  return false;
}

const MIN: Record<Axis, 'minX' | 'minY' | 'minZ'> = { x: 'minX', y: 'minY', z: 'minZ' };
const MAX: Record<Axis, 'maxX' | 'maxY' | 'maxZ'> = { x: 'maxX', y: 'maxY', z: 'maxZ' };

/** Die Grenzen des Quaders auf einer Achse: von `pos - unten` bis `pos + oben`. */
function spanne(pos: Vec3, unten: Vec3, oben: Vec3, axis: Axis): [number, number] {
  return [pos[axis] - unten[axis], pos[axis] + oben[axis]];
}

/** Ob sich das um EPS geschrumpfte Intervall `a` mit `[min, max]` echt überlappt, wie bei Blöcken. */
function spannenUeberlappen(a: [number, number], min: number, max: number): boolean {
  return a[0] + EPS < max && min < a[1] - EPS;
}

/**
 * Die Fläche des nächsten festen Blocks in Richtung `d` (Vorzeichen) ab `kante`, oder `null`.
 * Es werden alle Schichten abgetastet, die der Quader auf dem Weg berührt.
 */
function naechsteBlockflaeche(
  world: World,
  axis: Axis,
  kante: number,
  d: number,
  quer: [Axis, Axis],
  u: [number, number],
  v: [number, number],
  unterkante: number | null,
): number | null {
  if (d > 0) {
    const erster = Math.floor(kante - EPS) + 1;
    const letzter = Math.floor(kante + d + EPS);
    for (let k = erster; k <= letzter; k++) {
      if (schichtFest(world, axis, k, quer, u, v, unterkante)) return k;
    }
  } else {
    const erster = Math.floor(kante + EPS) - 1;
    const letzter = Math.floor(kante + d - EPS);
    for (let k = erster; k >= letzter; k--) {
      if (schichtFest(world, axis, k, quer, u, v, unterkante)) return k + 1;
    }
  }
  return null;
}

/**
 * Die Fläche des nächsten Hindernis-Quaders in Richtung `d` ab `kante`, oder `null`. Ein Quader
 * zählt nur, wenn er quer zur Achse mit dem bewegten Quader überlappt und seine zugewandte
 * Fläche vor der Kante liegt. Ein Hindernis, in dem der Quader schon steckt, blockiert nicht –
 * dieselbe Regel wie bei Blöcken, man kommt heraus.
 */
function naechsteHindernisflaeche(
  hindernisse: readonly Aabb[],
  axis: Axis,
  kante: number,
  d: number,
  quer: [Axis, Axis],
  a: [number, number],
  b: [number, number],
): number | null {
  let naechste: number | null = null;
  for (const h of hindernisse) {
    if (!spannenUeberlappen(a, h[MIN[quer[0]]], h[MAX[quer[0]]])) continue;
    if (!spannenUeberlappen(b, h[MIN[quer[1]]], h[MAX[quer[1]]])) continue;
    if (d > 0) {
      const flaeche = h[MIN[axis]];
      if (flaeche < kante - EPS || flaeche > kante + d) continue;
      if (naechste === null || flaeche < naechste) naechste = flaeche;
    } else {
      const flaeche = h[MAX[axis]];
      if (flaeche > kante + EPS || flaeche < kante + d) continue;
      if (naechste === null || flaeche > naechste) naechste = flaeche;
    }
  }
  return naechste;
}

/**
 * Verschiebt `pos` entlang einer Achse um `d` und stoppt am ersten festen Block oder
 * Hindernis-Quader, je nachdem, was näher liegt. `unten`/`oben` geben je Achse an, wie weit der
 * Quader unter bzw. über `pos` reicht. Beim Anstoßen wird die Position genau auf die Fläche
 * gesetzt (keine Restdrift), sonst voll verschoben. Verändert `pos`; liefert, ob gestoppt wurde.
 */
function sweepAxis(
  world: World,
  pos: Vec3,
  unten: Vec3,
  oben: Vec3,
  axis: Axis,
  d: number,
  hindernisse: readonly Aabb[],
): boolean {
  if (d === 0) return false;
  const quer = QUER[axis];
  const a = spanne(pos, unten, oben, quer[0]);
  const b = spanne(pos, unten, oben, quer[1]);
  const u = blockRange(a[0], a[1]);
  const v = blockRange(b[0], b[1]);

  const kante = d > 0 ? pos[axis] + oben[axis] : pos[axis] - unten[axis];
  // Nur waagerecht zählt die verlängerte Höhe des Blocks unter dem Quader; senkrecht bleibt es
  // bei ganzen Blöcken, damit Landen und Stehen unverändert funktionieren (ADR 0022).
  const unterkante = axis === 'y' ? null : pos.y - unten.y;
  const block = naechsteBlockflaeche(world, axis, kante, d, quer, u, v, unterkante);
  const quader = naechsteHindernisflaeche(hindernisse, axis, kante, d, quer, a, b);
  let flaeche = block;
  if (quader !== null && (flaeche === null || (d > 0 ? quader < flaeche : quader > flaeche))) flaeche = quader;

  if (flaeche === null) {
    pos[axis] += d;
    return false;
  }
  pos[axis] = d > 0 ? flaeche - oben[axis] : flaeche + unten[axis];
  return true;
}

/**
 * Steckt die Unterkante des Quaders um mehr als EPS in einem festen Block, wird der Quader auf
 * dessen Oberkante gehoben, ein Block je Aufruf. Die Schwerkraft zieht jeden Tick nach unten, und
 * die Abtastung nach unten überspringt den Block, in dem die Kante schon steckt; ohne diese Hebung
 * wäre ein Fußpunkt im Block der einzige Weg durch den Boden (ADR 0007). Seitlich und nach oben
 * bleibt es dabei: Blöcke, in denen der Quader steckt, blockieren nicht. Verändert `pos`; liefert,
 * ob gehoben wurde.
 */
function ausDemBodenHeben(world: World, pos: Vec3, unten: Vec3, oben: Vec3): boolean {
  const quer = QUER.y;
  const u = blockRange(pos.x - unten.x, pos.x + oben.x);
  const v = blockRange(pos.z - unten.z, pos.z + oben.z);
  const k = Math.floor(pos.y - unten.y + EPS);
  if (!schichtFest(world, 'y', k, quer, u, v, null)) return false;
  pos.y = k + 1 + unten.y;
  return true;
}

/**
 * Verschiebt einen Quader mit Fußpunkt `pos` (Mitte der Grundfläche), halber Breite `halfWidth`
 * und Höhe `height` um `delta`, achsenweise erst y, dann x, dann z, und stoppt an festen Blöcken
 * und an den Quadern in `hindernisse` (etwa dem Avatar, wenn ein Tier geht; ADR 0018). Vorher
 * wird ein im Boden steckender Fußpunkt auf die Blockoberkante gehoben; das zählt als Treffer
 * auf y. Alle vom Quader berührten Blöcke werden abgetastet, auch bei Schritten über mehrere
 * Blöcke. Reine Funktion: `pos`, `delta` und `hindernisse` bleiben unverändert, die neue Position
 * kommt zurück.
 */
export function moveAabb(
  world: World,
  pos: Vec3,
  halfWidth: number,
  height: number,
  delta: Vec3,
  hindernisse: readonly Aabb[] = [],
): MoveResult {
  const neu: Vec3 = { x: pos.x, y: pos.y, z: pos.z };
  const unten: Vec3 = { x: halfWidth, y: 0, z: halfWidth };
  const oben: Vec3 = { x: halfWidth, y: height, z: halfWidth };
  const gehoben = ausDemBodenHeben(world, neu, unten, oben);
  const hitY = sweepAxis(world, neu, unten, oben, 'y', delta.y, hindernisse) || gehoben;
  const hitX = sweepAxis(world, neu, unten, oben, 'x', delta.x, hindernisse);
  const hitZ = sweepAxis(world, neu, unten, oben, 'z', delta.z, hindernisse);
  return { pos: neu, hitX, hitY, hitZ };
}

/** Der Quader zu einem Fußpunkt: halbe Breite um die Mitte der Grundfläche, Höhe darüber. */
export function aabbAt(pos: Vec3, halfWidth: number, height: number): Aabb {
  return {
    minX: pos.x - halfWidth,
    maxX: pos.x + halfWidth,
    minY: pos.y,
    maxY: pos.y + height,
    minZ: pos.z - halfWidth,
    maxZ: pos.z + halfWidth,
  };
}

/** Echte Überlappung auf allen drei Achsen; bloßes Berühren zählt nicht. */
export function aabbsOverlap(a: Aabb, b: Aabb): boolean {
  return a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY && a.minZ < b.maxZ && b.minZ < a.maxZ;
}

/**
 * Der kürzeste Weg, mit dem `quader` aus `hindernis` herauskommt: genau eine Achse, die mit der
 * geringsten Durchdringung (bei Gleichstand x vor y vor z), und auf ihr die Richtung von der
 * Mitte des Hindernisses weg – nie hindurch. `margin` ist der Spalt, der danach zwischen den
 * Flächen bleibt. Ohne Überlappung der Nullvektor. Warum nur eine Achse und nur weg vom
 * Hindernis: ADR 0018.
 */
export function separationDelta(quader: Aabb, hindernis: Aabb, margin: number): Vec3 {
  const delta: Vec3 = { x: 0, y: 0, z: 0 };
  if (!aabbsOverlap(quader, hindernis)) return delta;
  let tiefe = Infinity;
  for (const axis of ['x', 'y', 'z'] as const) {
    const mitteQuader = (quader[MIN[axis]] + quader[MAX[axis]]) / 2;
    const mitteHindernis = (hindernis[MIN[axis]] + hindernis[MAX[axis]]) / 2;
    // Nach plus hinaus, wenn die Mitte des Quaders auf der Plus-Seite liegt, sonst nach minus.
    const d =
      mitteQuader >= mitteHindernis
        ? hindernis[MAX[axis]] - quader[MIN[axis]]
        : -(quader[MAX[axis]] - hindernis[MIN[axis]]);
    if (Math.abs(d) < tiefe) {
      tiefe = Math.abs(d);
      delta.x = delta.y = delta.z = 0;
      delta[axis] = d + Math.sign(d) * margin;
    }
  }
  return delta;
}
